import { z } from 'zod';

import { IntelligenceInsightSchema, IntelligencePersonaSchema } from './schemas';

/**
 * Stored Inteligencia reports (Max). Shapes follow
 * docs/backend-api-contract.v1.md, "Inteligencia reports (Max)". Only the lead
 * signer is stored; the client derives the signer of each finding.
 */

export const ReportNarrationSchema = z.enum(['written', 'quiet', 'off', 'cap_reached', 'busy', 'unavailable']);
export type ReportNarration = z.infer<typeof ReportNarrationSchema>;

export const ReportSignerSchema = z.object({
  personaId: IntelligencePersonaSchema.shape.personaId,
  name: z.string(),
  title: z.string(),
});
export type ReportSigner = z.infer<typeof ReportSignerSchema>;

export const IntelligenceReportSummarySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  headline: z.string(),
  signer: ReportSignerSchema,
  insightCount: z.number().int(),
  narration: ReportNarrationSchema,
});
export type IntelligenceReportSummary = z.infer<typeof IntelligenceReportSummarySchema>;

export const IntelligenceReportSchema = IntelligenceReportSummarySchema.extend({
  range: z.object({ from: z.string(), to: z.string() }),
  reviewed: z.object({
    bookings: z.number().int(),
    charges: z.number().int(),
    expenses: z.number().int(),
    customers: z.number().int(),
  }),
  insights: z.array(IntelligenceInsightSchema),
  personas: z.array(IntelligencePersonaSchema),
  /** Only for cap_reached, busy and unavailable. */
  notice: z.string().optional(),
});
export type IntelligenceReport = z.infer<typeof IntelligenceReportSchema>;

export const IntelligenceUsageSchema = z.object({
  /** YYYY-MM on the Panama calendar. */
  month: z.string(),
  timeZone: z.literal('America/Panama'),
  usedCents: z.number(),
  /** The plan's monthly allowance: US$20 on Max, three times that on Ultra, US$5 during an unpaid trial. */
  capCents: z.number().int().positive(),
  capReached: z.boolean(),
  resetsAt: z.string(),
});
export type IntelligenceUsage = z.infer<typeof IntelligenceUsageSchema>;

export const IntelligenceReportListResponseSchema = z.object({
  data: z.array(IntelligenceReportSummarySchema),
  usage: IntelligenceUsageSchema.optional(),
});
export type IntelligenceReportListResponse = z.infer<typeof IntelligenceReportListResponseSchema>;

export const IntelligenceReportResponseSchema = z.object({ data: IntelligenceReportSchema });

export const RequestIntelligenceReportResponseSchema = z.object({
  data: IntelligenceReportSchema,
  /** True when a report younger than 30 minutes answered the request. */
  reused: z.boolean(),
});
export type RequestIntelligenceReportResponse = z.infer<typeof RequestIntelligenceReportResponseSchema>;
